<template>
  <section
    data-component="SupportChatModalBody"
    class="support-chat-modal-body"
    ref="topElement"
  >
    <!-- move to DXP -->
    <CardInfoComponent
      class="info-card"
      icon="icon-telefono"
    >
      {{ t('components.support-chat-call.schedule') }}
    </CardInfoComponent>

    <div
      class="title"
      v-if="false"
    >
      {{ t('components.support-chat-call.chat-label') }}
    </div>

    <div class="title">
      {{ t('components.support-chat-call.call-us-label') }}
    </div>

    <!-- Input para escribir tu nombre -->
    <v-form
      ref="$form"
      v-model="formValid"
      validate-on="input lazy"
    >
      <div class="input-container">
        <BaseInput
          :label="t('components.support-chat-call.name-input-label')"
          id="name"
          v-model="name"
          :maxlength="30"
          @keydown="filterInputName"
          :placeholder="t('components.support-chat-call.name-placeholder')"
          :rules="[
            validationRules.required,
            validationRules.characterLimit,
            validationRules.validName
          ]"
        />
        <BaseInput
          :label="t('components.support-chat-call.phone-input-label')"
          id="phone"
          class="phone-input"
          v-model="phone"
          icon="icon-telefono"
          :iconText="prefix"
          :placeholder="t('components.support-chat-call.phone-placeholder')"
          :maxlength="maxLength"
          @keydown="filterInput"
          :rules="[validationRules.required, validationRules.validPhone]"
          :tooltip="t('components.support-chat-call.phone-tooltip')"
        />
      </div>
      <FoldingText
        class="privacy-text"
        :lineClamp="1"
        :text="t('components.support-chat-call.description-before-checkbox')"
      />

      <BaseCheckBox
        id="consent"
        class="privacy-checkbox"
        :label="t('components.support-chat-call.data-consent-checkbox')"
        v-model="consent"
        :rules="[validationRules.checkRequired]"
      />

      <BaseButton
        type="highlight"
        @click="triggerSubmit"
      >
        {{ t('components.support-chat-call.call-me-button') }}
      </BaseButton>
    </v-form>

    <div class="call-us">
      <div class="phone-text">
        {{ t('components.support-chat-call.free-phone-number-label') }}
      </div>

      <p
        v-if="sharedGtmDataStore.hiringNumber"
        class="hiring_number"
      >
        <span>
          {{ t('hiring.common.hiring-number') }}
        </span>
        <b> {{ sharedGtmDataStore.hiringNumber }} </b>
      </p>

      <p
        v-if="supportModalStore.selectedQuotationNumber"
        class="quotation-number"
      >
        {{ t('components.support-chat-call.quotation-number') }}
        <strong>{{ supportModalStore.selectedQuotationNumber }}</strong>
      </p>

      <p
        v-if="supportModalStore.personalizedQuotationNumber"
        class="quotation-number"
      >
        {{ t('components.support-chat-call.quotation-number-with-warranties') }}
        <strong>{{ supportModalStore.personalizedQuotationNumber }}</strong>
      </p>

      <div class="phone-number">
        <a
          atphone
          :name="phoneNumberId"
          :id="phoneNumberId"
          :href="phoneNumberLink"
          @click="redirectToPhoneNumber"
          class="atphone"
        >
          {{ phoneNumber }}
        </a>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import { BaseButton, BaseCheckBox, BaseInput, FoldingText } from '@/modules/core/components'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useBusinessStore } from '@/modules/core/stores/businessStore'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'

  import { SPAIN_PREFIX } from '@/modules/core/constants/common'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { PHONE_LINK } from '@/modules/core/models/IdHTMLItems'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'
  import UserTypesEnum from '@/modules/core/models/UserTypesEnum'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'

  interface PropsInterface {
    contactPhone: string
  }

  const props = defineProps<PropsInterface>()
  const { t } = useI18n()
  const commonStore = useCommonStore()
  const gtmStore = useGTMStore()
  const businessStore = useBusinessStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const loadingStore = useLoadingStore()
  const supportModalStore = useSupportModalStore()
  const { required, validPhone, validName, characterLimit } = useRules()

  const name = ref('')
  const phone = ref('')
  const consent = ref(false)

  const prefix = ref(SPAIN_PREFIX)
  const maxLength = computed(() => (prefix.value === SPAIN_PREFIX ? 9 : 15))

  const $form = ref<HTMLFormElement>()
  const formValid = ref(true)
  const topElement = ref<HTMLElement | null>(null)
  const phoneNumber = computed(
    () => props.contactPhone || t('components.support-chat-call.free-phone-number')
  )
  const phoneNumberLink = computed(() => 'tel:' + phoneNumber.value)
  const gtmPrefix = 'support_chat_modal_'
  const phoneNumberId = computed(() => gtmPrefix + PHONE_LINK)

  const gtmCallmebackFormSentEvent = computed(() => ({
    event: GTMEventType.CALLMEBACK_FORM,
    ramo: businessStore.getBusinessLineName()
      ? SpanishBranches[businessStore.getBusinessLineName() as keyof typeof SpanishBranches]
      : undefined,
    user_phone: phone.value,
    num_solicitud: sharedGtmDataStore.hiringNumber,
    producto: businessStore.getInsurance()?.productName ?? undefined,
    item_id: businessStore.getInsurance()?.productCode ?? undefined,
    value: sharedGtmDataStore.policyValue,
    num_asegurados: sharedGtmDataStore.forWhomPeople ?? sharedGtmDataStore.forWhomPets,
    cobert_adicionales: sharedGtmDataStore.additionalCoverages ?? false,
    otra_comp: sharedGtmDataStore.anotherCompany,
    capital: sharedGtmDataStore.capital,
    tipo_beneficiario: sharedGtmDataStore.beneficiaries_type,
    num_beneficiarios: sharedGtmDataStore.beneficiaries_number,
    num_mascotas: sharedGtmDataStore.forWhomPets,
    tipo_viaje: sharedGtmDataStore.travel_type,
    destino_viaje: sharedGtmDataStore.travel_destination,
    duracion_viaje: sharedGtmDataStore.travel_duration
  }))

  defineExpose({ submit })
  const emit = defineEmits(['close', 'submit'])

  const validationRules = {
    required,
    validName,
    checkRequired: (value: boolean) => required(value, 'common.validations.checked'),
    validPhone: (value: string) => validPhone(value, prefix.value),
    characterLimit: (value: string) => characterLimit(value, 30)
  }

  const filterInput = (event: KeyboardEvent) => {
    const regex = /^[0-9+]$/
    const validKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Tab', 'Delete', 'Ctrl', 'v']

    const currentValue: string = (event.target as HTMLInputElement).value

    const plusAlreadyPresent =
      currentValue.includes('+') && event.key === '+' && currentValue.length !== 0

    if (
      (!regex.test(event.key) && !validKeys.includes(event.key)) ||
      plusAlreadyPresent ||
      (event.key === 'v' && !(event.ctrlKey || event.metaKey))
    ) {
      event.preventDefault()
    }
  }

  const filterInputName = (event: KeyboardEvent) => {
    const regex = /^[a-zA-ZñÑçÇáéíóúÁÉÍÓÚäÄëËïÏöÖüÜ\s+]$/
    const validKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Tab', 'Delete']

    if (
      (!regex.test(event.key) && !validKeys.includes(event.key)) ||
      (event.key === 'v' && (event.ctrlKey || event.metaKey))
    ) {
      event.preventDefault()
    }
  }

  async function submit() {
    try {
      await commonStore.clickToCall(name.value, prefix.value?.slice(1), phone.value)
      gtmStore.sendEvent(gtmCallmebackFormSentEvent.value)
      commonStore.getUserInfo().type = UserTypesEnum.QUALIFIED
      return { ok: true }
    } catch {
      topElement.value?.scrollIntoView({ behavior: 'smooth' })
      return { ok: false }
    }
  }

  async function triggerSubmit() {
    const { valid: isValid } = await $form.value!.validate()
    if (isValid) emit('submit', true)
  }

  async function redirectToPhoneNumber(e: Event) {
    e.preventDefault()
    gtmStore.sendEvent({
      event: GTMEventType.CLICK_PHONE_NUMBER,
      click_name: phoneNumberId.value,
      click_id: phoneNumberId.value,
      btn_label: phoneNumber.value,
      num_solicitud: sharedGtmDataStore.hiringNumber,
      producto: businessStore.getInsurance()?.productName ?? undefined,
      item_id: businessStore.getInsurance()?.productCode ?? undefined,
      ramo: sharedGtmDataStore.ramo,
      value: sharedGtmDataStore.policyValue,
      num_asegurados: sharedGtmDataStore.forWhomPeople,
      cobert_adicionales: sharedGtmDataStore.additionalCoverages ?? false,
      otra_comp: sharedGtmDataStore.anotherCompany,
      capital: sharedGtmDataStore.capital,
      tipo_beneficiario: sharedGtmDataStore.beneficiaries_type,
      num_beneficiarios: sharedGtmDataStore.beneficiaries_number,
      num_mascotas: sharedGtmDataStore.forWhomPets,
      tipo_viaje: sharedGtmDataStore.travel_type,
      destino_viaje: sharedGtmDataStore.travel_destination,
      duracion_viaje: sharedGtmDataStore.travel_duration
    })
    commonStore.getUserInfo().type = UserTypesEnum.QUALIFIED
    setTimeout(() => {
      const enlace = document.createElement('a')
      enlace.href = phoneNumberLink.value
      enlace.click()
    }, 200) // give time to gtm
  }

  onMounted(async () => {
    loadingStore.stopLoading()
  })
</script>

<style lang="scss" scoped>
  .support-chat-modal-body {
    width: $size-full;
    max-width: $size-375;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: $gap-l;
    background-color: white;

    .title {
      color: $color-primary-dark;
      font-size: $font-size-20;
      font-style: normal;
      font-weight: $font-weight-500;
      line-height: $line-height-130p;
      margin-bottom: $spacing-m;
    }

    .BaseButton + .title {
      border-top: solid 1px $color-neutral-light;
      padding-top: $spacing-xl;
    }

    .info-card {
      margin-bottom: 0;
    }

    .privacy-checkbox,
    .privacy-text {
      font-size: $font-size-s;
    }

    .privacy-checkbox {
      font-weight: $font-weight-500;
    }

    .privacy-text {
      display: flex;
      flex-direction: row;
      font-weight: $font-weight-400;

      :deep(.folding-text) {
        max-width: 80%;
      }

      :deep(.expand-button) {
        margin-left: auto;
        align-self: baseline;
      }
    }

    .input-container {
      display: flex;
      flex-direction: column;
      gap: $gap-m;
      margin-bottom: $spacing-m;

      .phone-input {
        :deep(.v-field__prepend-inner) {
          i::before,
          span {
            font-weight: $font-weight-700;
          }
        }
      }
    }

    .call-us {
      border-top: solid 1px $color-neutral-light;
      border-bottom: solid 1px $color-neutral-light;
      padding: $spacing-s 0;
      margin: $spacing-s 0;

      .hiring_number {
        color: $color-neutral-dark;
        text-align: center;
      }

      .quotation-number {
        color: $color-neutral-dark;
        text-align: center;
        font-size: $font-size-m;
        padding-top: $spacing-s;
      }

      .phone-text {
        text-align: center;
        color: $color-primary-dark;
        font-size: $font-size-l;
        font-style: normal;
        font-weight: $font-weight-500;
        line-height: $line-height-140p;
      }

      .phone-number {
        text-align: center;
        padding-top: $spacing-s;

        a {
          font-size: $font-size-l;
          font-style: normal;
          font-weight: $font-weight-700;
          line-height: $line-height-140p;
          color: $color-primary-dark;
          text-decoration: none;
        }
      }
    }

    .flexi_column {
      display: flex;
      flex-direction: column;
    }

    :deep(.v-selection-control) {
      align-items: start;

      .v-label {
        font-size: $font-size-m;
        line-height: $line-height-140p;
        color: $color-neutral-dark;
        opacity: 1;
      }
    }
  }
</style>
